import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import UserSidebar from "./UserSidebar";
import { Menu, Search, Bell, User as UserIcon, ChevronDown, Settings, HelpCircle, LogOut } from "lucide-react";
import { useUserAuth } from "../../../services/user/UserAuthServices";
import { useComingSoon } from "../../../utils/useComingSoon";

export default function UserLayout({ children }) {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user, userLogout } = useUserAuth();
  const { showComingSoon } = useComingSoon();

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      setDropdownOpen(false);
      await userLogout(navigate);
    } catch (error) {
      console.error("Logout failed:", error);
      localStorage.removeItem("user");
      localStorage.removeItem("user_token");
      navigate("/login", { replace: true });
    }
  };

  const displayName = user?.full_name || "User";
  const displayEmail = user?.email || "";

  return (
    <div className="h-screen min-h-[100dvh] flex flex-col bg-[#F9FAFB] dark:bg-[#111111] overflow-hidden">
      {/* Mobile/Tablet Header */}
      <header className="lg:hidden sticky top-0 z-40 bg-white dark:bg-[#1A1A1A] border-b border-[#E5E7EB] dark:border-[#2A2A2A] px-4 py-4 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#262626]">
          <Menu size={22} className="text-[#111111] dark:text-white" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-[#62397f] to-[#7C3AED] flex items-center justify-center shadow-md">
            <img src="/static/images/logo.webp" alt="Logo" className="w-8 h-8 object-contain" />
          </div>
          <span className="font-medium text-[#111111] dark:text-white text-sm font-serif">St. Stephen's</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Mobile Notification */}
          <button
            className="relative p-2 rounded-lg hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
            onClick={() => showComingSoon({
              title: "Notifications Coming Soon!",
              message: "We're building the notifications feature. Stay tuned!"
            })}
          >
            <Bell size={18} className="text-[#666666] dark:text-[#A0A0A0]" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
          {/* Mobile Profile Avatar */}
          <div className="relative" ref={dropdownOpen ? dropdownRef : undefined}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-10 h-10 bg-gradient-to-br from-[#62397f] to-[#7C3AED] rounded-full flex items-center justify-center"
            >
              <UserIcon size={18} className="text-white" />
            </button>
          </div>
        </div>
      </header>

      {/* User Dashboard Header (Desktop Only) */}
      <header className="hidden lg:flex sticky top-0 z-30 bg-white dark:bg-[#1A1A1A] border-b border-[#E5E7EB] dark:border-[#2A2A2A] px-8 py-4 items-center justify-between">
        <div className="flex flex-1 max-w-xl">
          <div className="relative w-full">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0]" />
            <input
              type="text"
              placeholder="Search bookings, invoices..."
              readOnly
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A] bg-[#F9FAFB] dark:bg-[#262626] text-[#111111] dark:text-white placeholder-[#666666] dark:placeholder-[#A0A0A0] focus:outline-none focus:ring-2 focus:ring-[#62397f]/50 transition-all text-sm cursor-pointer"
              onClick={() => showComingSoon({
                title: "Search Coming Soon!",
                message: "We're building the search feature. Stay tuned!"
              })}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Notifications */}
          <button
            className="relative p-2 rounded-lg hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
            onClick={() => showComingSoon({
              title: "Notifications Coming Soon!",
              message: "We're building the notifications feature. Stay tuned!"
            })}
          >
            <Bell size={20} className="text-[#666666] dark:text-[#A0A0A0]" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>

          {/* User Menu with Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-[#62397f] to-[#7C3AED] rounded-full flex items-center justify-center">
                <UserIcon size={18} className="text-white" />
              </div>
              <div className="flex flex-col items-start leading-tight">
                <span className="text-sm font-semibold text-[#111111] dark:text-white">{displayName}</span>
                <span className="text-[11px] text-[#666666] dark:text-[#A0A0A0]">Customer</span>
              </div>
              <ChevronDown size={14} className="text-[#666666] dark:text-[#A0A0A0]" />
            </button>
          </div>
        </div>
      </header>

      {/* Profile Dropdown (shared for desktop & mobile) */}
      {dropdownOpen && (
        <div
          ref={dropdownRef}
          className="fixed right-4 top-16 lg:top-[72px] w-56 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl shadow-lg overflow-hidden z-[60]"
        >
          {/* User Info */}
          <div className="px-4 py-3 border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
            <p className="text-sm font-semibold text-[#111111] dark:text-white">{displayName}</p>
            {displayEmail && (
              <p className="text-xs text-[#666666] dark:text-[#A0A0A0] truncate">{displayEmail}</p>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <button
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#111111] dark:text-white hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
              onClick={() => {
                setDropdownOpen(false);
                showComingSoon({
                  title: "Profile Coming Soon!",
                  message: "We're building the profile feature. Stay tuned!"
                });
              }}
            >
              <UserIcon size={16} className="text-[#666666] dark:text-[#A0A0A0]" />
              My Profile
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#111111] dark:text-white hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
              onClick={() => {
                setDropdownOpen(false);
                navigate("/dashboard/settings");
              }}
            >
              <Settings size={16} className="text-[#666666] dark:text-[#A0A0A0]" />
              Settings
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#111111] dark:text-white hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
              onClick={() => {
                setDropdownOpen(false);
                showComingSoon({
                  title: "Help & Support Coming Soon!",
                  message: "We're building the help & support feature. Stay tuned!"
                });
              }}
            >
              <HelpCircle size={16} className="text-[#666666] dark:text-[#A0A0A0]" />
              Help & Support
            </button>
          </div>

          {/* Logout */}
          <div className="border-t border-[#E5E7EB] dark:border-[#2A2A2A] py-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block">
          <UserSidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        </div>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 bg-black/50 z-50 animate-in fade-in duration-200" onClick={() => setSidebarOpen(false)}>
            <div className="absolute left-0 top-0 bottom-0 w-64 bg-white dark:bg-[#1A1A1A] animate-in slide-in-from-left duration-300" onClick={(e) => e.stopPropagation()}>
              <UserSidebar onClose={() => setSidebarOpen(false)} />
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto bg-[#F9FAFB] dark:bg-[#111111]">
          <div className="min-h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
